import { loadTtfFont } from '@/fonts/fonts';
import type { CellJustify, CellVerticalAlign } from '@/model/cellStyle';
import {
  DEFAULT_RICH_TEXT_STYLE,
  type RichText,
  type RichTextRun,
  type RichTextStyle,
} from '@/model/richText';
import { type StoredStyle, computeStyle } from '@/model/types';
import {
  type EmbeddedFont,
  type FontInput,
  type PDF,
  Standard14Font,
} from '@libpdf/core';

export type TextLayoutSettings = {
  width: number;
  height: number;
  justify: CellJustify;
  verticalAlign: CellVerticalAlign;
  textWrap: boolean;
};

export type RunBox = {
  width: number;
  height: number;
  textHeight: number;
  word: string;
  style: RichTextStyle;
  font: FontInput;
  newLine: boolean;
  whitespace: boolean;
  lastBox: boolean;
};

export type LayoutLine = {
  /** The height of the tallest run */
  height: number;
  /** The total width of the line (sum of all run widths) */
  width: number;
  /** The runs that make up this line */
  runs: RunBox[];
};

export type LayoutLines = {
  /** The lines of text */
  lines: LayoutLine[];
  /** The total height of the text */
  height: number;
  /** The total height of the text excluding the last line */
  heightWithoutLastLine: number;
  /** The height of the last line */
  lastLineHeight: number;
  /** The total width of the text */
  width: number;
};

async function getFont(
  pdf: PDF,
  { font, bold, italic }: RichTextStyle,
): Promise<EmbeddedFont | Standard14Font> {
  if (font === 'Helvetica') {
    if (bold && italic) return Standard14Font.of('Helvetica-BoldOblique');
    if (bold) return Standard14Font.of('Helvetica-Bold');
    if (italic) return Standard14Font.of('Helvetica-Oblique');
    return Standard14Font.of('Helvetica');
  }
  if (font === 'Courier') {
    if (bold && italic) return Standard14Font.of('Courier-BoldOblique');
    if (bold) return Standard14Font.of('Courier-Bold');
    if (italic) return Standard14Font.of('Courier-Oblique');
    return Standard14Font.of('Courier');
  }
  if (font === 'Times') {
    if (bold && italic) return Standard14Font.of('Times-BoldItalic');
    if (bold) return Standard14Font.of('Times-Bold');
    if (italic) return Standard14Font.of('Times-Italic');
    return Standard14Font.of('Times-Roman');
  }
  const ttf = await loadTtfFont(font, italic, bold);
  return pdf.embedFont(ttf);
}

/**
 * Newlines and whitespace are treated as separate words. Example:
 * "Hello  \n\n  World" -> ['Hello', '  ', '', '\n', '', '\n', '', '  ', 'World']
 * (gotta filter out the empty strings)
 */
function getWords(text: string): string[] {
  return text.split(/(\r\n|[\r\n]|[^\S\r\n]+)/);
}

async function getRunBoxes({
  cellStyle,
  run,
  pdf,
  settings,
}: {
  cellStyle: StoredStyle<RichTextStyle>;
  run: RichTextRun;
  pdf: PDF;
  settings: TextLayoutSettings;
}): Promise<RunBox[]> {
  const { style: rs } = run;
  const style = computeStyle(DEFAULT_RICH_TEXT_STYLE, cellStyle, rs);
  const { size } = style;
  const font = await getFont(pdf, style);
  const textHeight = font.heightAtSize(size);
  const words = getWords(run.text);
  const runBoxes: RunBox[] = [];
  for (const word of words) {
    if (word.length === 0) continue;
    const width = font.widthOfTextAtSize(word, size);
    const newLine = word === '\n' || word === '\r\n' || word === '\r';
    const whitespace = !newLine && word.trim().length === 0;
    const runBox: RunBox = {
      width,
      height: size,
      textHeight,
      word,
      style,
      font: font instanceof Standard14Font ? font.name : font,
      newLine,
      whitespace,
      lastBox: false,
    };
    runBoxes.push(runBox);
  }

  return runBoxes;
}

function orderRunBoxes(
  runBoxes: RunBox[],
  settings: TextLayoutSettings,
): RunBox[][] {
  const { textWrap } = settings;

  const runBoxRows: RunBox[][] = [];
  let runBoxRow: RunBox[] = [];
  let totalWidth = 0;
  for (let i = 0; i < runBoxes.length; i++) {
    const runBox = runBoxes[i];
    if (!runBox) continue; // keep ts happy
    if (runBox.newLine) {
      runBox.lastBox = true;
      runBoxRow.push(runBox);
      runBoxRows.push(runBoxRow);
      runBoxRow = [];
      totalWidth = 0;
      continue;
    }
    if (runBox.whitespace) {
      const nextRunBox = runBoxes[i + 1];
      if (!nextRunBox || nextRunBox.newLine) {
        runBox.lastBox = true;
        runBoxRow.push(runBox);
        runBoxRows.push(runBoxRow);
        runBoxRow = [];
        totalWidth = 0;
        continue;
      }
      if (
        textWrap &&
        totalWidth + runBox.width + nextRunBox.width > settings.width
      ) {
        runBox.lastBox = true;
        runBoxRow.push(runBox);
        runBoxRows.push(runBoxRow);
        runBoxRow = [];
        totalWidth = 0;
        continue;
      }
      runBoxRow.push(runBox);
      totalWidth += runBox.width;
      continue;
    }
    runBoxRow.push(runBox);
    totalWidth += runBox.width;
  }
  if (runBoxRow.length > 0) {
    const lastRunBox = runBoxRow[runBoxRow.length - 1];
    if (lastRunBox) lastRunBox.lastBox = true;
    runBoxRows.push(runBoxRow);
  }

  return runBoxRows;
}

function toLayoutLines(runBoxes: RunBox[][]): LayoutLine[] {
  const layoutLines: LayoutLine[] = [];

  for (const runBoxRow of runBoxes) {
    const layoutLine: LayoutLine = {
      height: 0,
      width: 0,
      runs: [...runBoxRow],
    };
    for (const runBox of runBoxRow) {
      if (runBox.whitespace && runBox.lastBox) continue;
      layoutLine.height = Math.max(layoutLine.height, runBox.height);
      layoutLine.width += runBox.width;
    }
    layoutLines.push(layoutLine);
  }

  return layoutLines;
}

export async function layoutRichText({
  richText,
  cellStyle,
  pdf,
  settings,
}: {
  richText: RichText;
  cellStyle: StoredStyle<RichTextStyle>;
  pdf: PDF;
  settings: TextLayoutSettings;
}): Promise<LayoutLines> {
  const runBoxes = (
    await Promise.all(
      richText
        .getRuns()
        .map(run => getRunBoxes({ run, cellStyle, pdf, settings })),
    )
  ).flat();
  const runBoxRows = orderRunBoxes(runBoxes, settings);
  const lines = toLayoutLines(runBoxRows);

  return {
    lines,
    height: lines.reduce((acc, line) => acc + line.height, 0),
    heightWithoutLastLine: lines
      .slice(0, lines.length - 1)
      .reduce((acc, line) => acc + line.height, 0),
    lastLineHeight: lines[lines.length - 1]?.height ?? 0,
    width: lines.reduce((acc, line) => Math.max(acc, line.width), 0),
  };
}
